/* Fifth Collective — Diamond Bag cart icon.
   A champagne-gold wire handbag holding a Koh-i-Noor style oval brilliant that
   revolves slowly and swells in a pronounced pulse. One high-DPI canvas per icon.

   Contract with fifth-collective-cart-drawer-experience.js (keep intact):
   - host [data-cc-cart-motion] exposes host.__ccCartMotion.triggerExternalReceive()
     and .triggerPress();
   - triggerExternalReceive() publishes window.__ccCartReceiveStartedAt and
     window.__ccCartReceiveDuration, which time the cart-count bounce;
   - window.__ccCartAmbientHoldUntil (set by the add-to-cart flight) calms the
     ambient pulse so the receive moment reads clearly;
   - window.__fcKohinoor exposes the stone renderer (draw), its shared turn
     (angleAt) and geometry (stone), so the add-to-cart flight flies this stone.
   Shopify replaces #cart-icon-bubble after cart updates; every timeline here runs
   on a shared clock so a fresh canvas continues exactly where the old one was. */
(() => {
  if (window.__fifthCollectiveCartMotionLoaded) return;
  window.__fifthCollectiveCartMotionLoaded = true;

  const reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
  const TAU = Math.PI * 2;
  const clamp = (v, min = 0, max = 1) => Math.min(max, Math.max(min, v));
  const mix = (a, b, t) => a + (b - a) * t;
  const smoother = (t) => {
    t = clamp(t);
    return t * t * t * (t * (t * 6 - 15) + 10);
  };
  const bell = (t) => Math.sin(Math.PI * clamp(t));

  const EPOCH = window.__ccCartMotionEpoch || (window.__ccCartMotionEpoch = performance.now());
  const REVOLUTION = 9000;          // one slow turn of the diamond
  const PULSE = 3800;               // grow, then settle back
  const SWEEP_EVERY = 6800;         // light travelling across the gold
  const SWEEP_LENGTH = 1700;
  const RECEIVE_DURATION = 1400;    // shared with the cart-count bounce
  const PRESS_DURATION = 360;

  /* ---------- Bag geometry, in a 100 × 100 drawing space ---------- */
  const BODY = { tl: [20.5, 33], tr: [79.5, 33], br: [88, 87.5], bl: [12, 87.5], rTop: 3.2, rBottom: 4.2 };
  const sideX = (y, left) => {
    const a = left ? BODY.tl : BODY.tr;
    const b = left ? BODY.bl : BODY.br;
    return mix(a[0], b[0], (y - a[1]) / (b[1] - a[1]));
  };
  const FLAP_Y = 56;
  const V_POINT = [50, 77];
  const GROMMETS = [[33, 38.6], [67, 38.6]];
  /* Girdle centre and semi-major radius. The eye sits about 10° above the girdle
     (tilt), low enough that the whole stone reads — table, crown, girdle and the
     pavilion tapering to its culet in the flap's V. With the pulse the stone nods
     a little further toward the viewer (nod), so the table and the pavilion trade
     places as it turns. pivotY is the stone's visual centre: it swells in place. */
  const STONE = { x: 50, y: 56.5, pivotY: 59, r: 21, tilt: 0.17, nod: 0.08 };

  const traceBody = (g) => {
    const { tl, tr, br, bl, rTop, rBottom } = BODY;
    g.moveTo(50, tl[1]);
    g.arcTo(tr[0], tr[1], br[0], br[1], rTop);
    g.arcTo(br[0], br[1], bl[0], bl[1], rBottom);
    g.arcTo(bl[0], bl[1], tl[0], tl[1], rBottom);
    g.arcTo(tl[0], tl[1], tr[0], tr[1], rTop);
    g.closePath();
  };

  const traceHandle = (g) => {
    g.moveTo(GROMMETS[0][0], GROMMETS[0][1] - 1.6);
    g.lineTo(33, 27);
    g.ellipse(50, 27, 17, 17, 0, Math.PI, TAU);
    g.lineTo(GROMMETS[1][0], GROMMETS[1][1] - 1.6);
  };

  const traceFlap = (g) => {
    g.moveTo(sideX(FLAP_Y, true), FLAP_Y);
    g.lineTo(26, FLAP_Y);
    g.bezierCurveTo(35.5, FLAP_Y, 43.5, 68.5, V_POINT[0], V_POINT[1]);
    g.bezierCurveTo(56.5, 68.5, 64.5, FLAP_Y, 74, FLAP_Y);
    g.lineTo(sideX(FLAP_Y, false), FLAP_Y);
  };

  /* ---------- The stone: a Koh-i-Noor style oval brilliant ----------
     Proportions follow the Koh-i-Noor as recut in 1852 (about 36.0 × 31.9 mm,
     shallow): an oval girdle, large table and the classic 57 facets —
     33 on the crown (table, 8 star, 8 bezel, 16 upper girdle) and
     24 on the pavilion (8 mains, 16 lower girdle). Gem space: x along the
     length, z across, y pointing down; girdle semi-axes 1 × 0.886. */
  const KOH = { b: 0.886, crown: 0.24, pavilion: 0.56, table: 0.56, star: 0.78, lower: 0.24 };
  const MAINS = 8;
  const Q = TAU / MAINS;
  const GEM = (() => {
    const v = [];
    const add = (p) => v.push(p) - 1;
    const oval = (angle, radius, y) => [Math.cos(angle) * radius, y, Math.sin(angle) * radius * KOH.b];
    const crownY = (r) => -KOH.crown * (1 - r) / (1 - KOH.table);
    const pavilionY = (r) => KOH.pavilion * (1 - r);
    const T = []; const S = []; const L = []; const G = [];
    for (let k = 0; k < MAINS; k += 1) {
      T.push(add(oval(k * Q, KOH.table, -KOH.crown)));
      S.push(add(oval((k + 0.5) * Q, KOH.star, crownY(KOH.star))));
      L.push(add(oval((k + 0.5) * Q, KOH.lower, pavilionY(KOH.lower))));
    }
    for (let j = 0; j < MAINS * 2; j += 1) G.push(add(oval(j * Q / 2, 1, 0)));
    const C = add([0, KOH.pavilion, 0]);

    /* The table is split into a 16-piece fan so the pavilion's reflected star
       pattern can show through it; all pieces share the table's normal. */
    const centre = add([0, -KOH.crown, 0]);
    const faces = [];
    for (let k = 0; k < MAINS; k += 1) {
      const a = v[T[k]];
      const b = v[T[(k + 1) % MAINS]];
      const mid = add([(a[0] + b[0]) / 2, a[1], (a[2] + b[2]) / 2]);
      faces.push({ v: [centre, T[k], mid] });
      faces.push({ v: [centre, mid, T[(k + 1) % MAINS]] });
    }
    for (let k = 0; k < MAINS; k += 1) {
      const n = (k + 1) % MAINS;
      const p = (k + MAINS - 1) % MAINS;
      const g0 = G[2 * k];
      const g1 = G[2 * k + 1];
      const g2 = G[(2 * k + 2) % (MAINS * 2)];
      faces.push({ v: [T[k], T[n], S[k]] });          // star
      faces.push({ v: [T[k], S[p], g0, S[k]] });      // bezel
      faces.push({ v: [S[k], g0, g1] });              // upper girdle
      faces.push({ v: [S[k], g1, g2] });
      faces.push({ v: [g0, L[k], C, L[p]] });         // pavilion main
      faces.push({ v: [g0, g1, L[k]] });              // lower girdle
      faces.push({ v: [g1, g2, L[k]] });
    }

    /* Outward normals and centres, once, in gem space (Newell's method keeps
       the gently curved four-point kites well behaved). */
    for (const face of faces) {
      const pts = face.v.map((i) => v[i]);
      let n = [0, 0, 0];
      for (let i = 0; i < pts.length; i += 1) {
        const a = pts[i];
        const b = pts[(i + 1) % pts.length];
        n[0] += (a[1] - b[1]) * (a[2] + b[2]);
        n[1] += (a[2] - b[2]) * (a[0] + b[0]);
        n[2] += (a[0] - b[0]) * (a[1] + b[1]);
      }
      const c = pts.reduce((acc, q) => [acc[0] + q[0], acc[1] + q[1], acc[2] + q[2]], [0, 0, 0]).map((k) => k / pts.length);
      const m = Math.hypot(n[0], n[1], n[2]) || 1;
      n = n.map((k) => k / m);
      if (n[0] * c[0] + n[1] * (c[1] - 0.12) + n[2] * c[2] < 0) n = n.map((k) => -k);
      face.normal = n;
      face.centre = c;
    }
    return { vertices: v, faces, girdle: G };
  })();

  /* ---------- Optics ----------
     Each visible facet is shaded by tracing the eye's ray: a Fresnel reflection
     off the surface, plus the refracted ray bouncing off two pavilion facets and
     leaving through the crown. Both sample a jeweller's studio (soft boxes over a
     dark floor, the camera itself dark), which gives the real diamond mosaic of
     white and black facets. Red, green and blue refract slightly differently
     (dispersion), so fire appears on its own. */
  const IOR = [2.407, 2.418, 2.451];
  const F0 = Math.pow((2.418 - 1) / (2.418 + 1), 2);
  const norm = (a) => { const m = Math.hypot(a[0], a[1], a[2]) || 1; return [a[0] / m, a[1] / m, a[2] / m]; };
  const dot = (a, b) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
  const reflect = (d, n) => { const k = 2 * dot(d, n); return [d[0] - k * n[0], d[1] - k * n[1], d[2] - k * n[2]]; };
  const refract = (d, n, eta) => {
    const cosi = -dot(d, n);
    const k = 1 - eta * eta * (1 - cosi * cosi);
    if (k < 0) return null;
    const f = eta * cosi - Math.sqrt(k);
    return [eta * d[0] + f * n[0], eta * d[1] + f * n[1], eta * d[2] + f * n[2]];
  };
  const smooth = (e0, e1, x) => { const t = clamp((x - e0) / (e1 - e0)); return t * t * (3 - 2 * t); };
  /* A jeweller's studio: two rings of soft boxes around the stone, dark ceiling
     and floor, and darkness toward the camera. Facets either catch a box
     (white) or the dark (black) — the contrast that reads as brilliance. */
  const STUDIO = [
    { d: norm([0.814, -0.523, 0.252]), cos: Math.cos(0.32), power: 5 },
    { d: norm([0.189, -0.523, 0.831]), cos: Math.cos(0.32), power: 5 },
    { d: norm([-0.625, -0.523, 0.579]), cos: Math.cos(0.32), power: 5 },
    { d: norm([-0.814, -0.523, -0.252]), cos: Math.cos(0.32), power: 5 },
    { d: norm([-0.189, -0.523, -0.831]), cos: Math.cos(0.32), power: 5 },
    { d: norm([0.625, -0.523, -0.579]), cos: Math.cos(0.32), power: 5 },
    /* A lower ring of six, so no turn of the stone ever falls fully dark. */
    { d: norm([0.871, -0.120, 0.476]), cos: Math.cos(0.3), power: 3.5 },
    { d: norm([0.023, -0.120, 0.993]), cos: Math.cos(0.3), power: 3.5 },
    { d: norm([-0.848, -0.120, 0.517]), cos: Math.cos(0.3), power: 3.5 },
    { d: norm([-0.871, -0.120, -0.476]), cos: Math.cos(0.3), power: 3.5 },
    { d: norm([-0.023, -0.120, -0.993]), cos: Math.cos(0.3), power: 3.5 },
    { d: norm([0.848, -0.120, -0.517]), cos: Math.cos(0.3), power: 3.5 }
  ];
  const studio = (d) => {
    let x = d[1] < 0 ? 0.08 + 0.08 * -d[1] : 0.01;
    for (const light of STUDIO) {
      const c = dot(d, light.d);
      if (c > light.cos - 0.06) x += light.power * smooth(light.cos - 0.06, light.cos + 0.015, c);
    }
    if (d[2] > 0.78) x *= 0.08;
    return x;
  };
  /* Where a ray inside the stone meets its surface, and that facet's outward
     normal: the table plane, the crown slope (16 facets around) or the pavilion
     cone (8 mains), each solved exactly on the oval. */
  const KC = (1 - KOH.table) / KOH.crown;
  const KP = 1 / KOH.pavilion;
  const facetNormal = (x, z, slope, count) => {
    const a = Math.round(Math.atan2(z / KOH.b, x) / (TAU / count)) * (TAU / count);
    return norm([Math.cos(a), slope, Math.sin(a) / KOH.b]);
  };
  const smallestRoot = (a, b, c) => {
    if (Math.abs(a) < 1e-9) return Math.abs(b) < 1e-9 ? [] : [-c / b];
    const disc = b * b - 4 * a * c;
    if (disc < 0) return [];
    const r = Math.sqrt(disc);
    return [(-b - r) / (2 * a), (-b + r) / (2 * a)];
  };
  const hitSurface = (p, d) => {
    let best = null;
    const consider = (t, normalAt) => {
      if (!(t > 1e-4) || (best && t >= best.t)) return;
      const q = [p[0] + d[0] * t, p[1] + d[1] * t, p[2] + d[2] * t];
      const n = normalAt(q);
      if (n) best = { t, q, n };
    };
    const radius = (q) => Math.hypot(q[0], q[2] / KOH.b);
    if (d[1] < 0) {
      consider((-KOH.crown - p[1]) / d[1], (q) => (radius(q) <= KOH.table + 1e-6 ? [0, -1, 0] : null));
    }
    const ex = d[0] * d[0] + (d[2] / KOH.b) ** 2;
    const exy = 2 * (p[0] * d[0] + p[2] * d[2] / (KOH.b * KOH.b));
    const exx = p[0] * p[0] + (p[2] / KOH.b) ** 2;
    const crownBase = 1 + KC * p[1];
    for (const t of smallestRoot(ex - (KC * d[1]) ** 2, exy - 2 * crownBase * KC * d[1], exx - crownBase * crownBase)) {
      consider(t, (q) => (q[1] <= 0 && q[1] >= -KOH.crown && radius(q) >= KOH.table - 1e-6 ? facetNormal(q[0], q[2], -KC, MAINS * 2) : null));
    }
    const pavilionBase = 1 - KP * p[1];
    for (const t of smallestRoot(ex - (KP * d[1]) ** 2, exy + 2 * pavilionBase * KP * d[1], exx - pavilionBase * pavilionBase)) {
      consider(t, (q) => (q[1] >= 0 && q[1] <= KOH.pavilion ? facetNormal(q[0], q[2], KP, MAINS) : null));
    }
    return best;
  };
  const DARK = [12, 14, 20];

  function convexHull(points) {
    const pts = points.slice().sort((a, b) => (a[0] - b[0]) || (a[1] - b[1]));
    const turn = (o, a, b) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
    const lower = [];
    for (const p of pts) {
      while (lower.length >= 2 && turn(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) lower.pop();
      lower.push(p);
    }
    const upper = [];
    for (let i = pts.length - 1; i >= 0; i -= 1) {
      const p = pts[i];
      while (upper.length >= 2 && turn(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) upper.pop();
      upper.push(p);
    }
    upper.pop();
    lower.pop();
    return lower.concat(upper);
  }

  /* ---------- Shared timelines ---------- */
  const receiveEnvelope = (now) => {
    const start = Number(window.__ccCartReceiveStartedAt);
    if (!Number.isFinite(start)) return { env: 0, raw: -1 };
    const raw = (now - start) / RECEIVE_DURATION;
    if (raw < 0 || raw > 1) return { env: 0, raw: -1 };
    /* Swell decisively, then settle more slowly; zero velocity at both ends. */
    const env = raw <= 0.34 ? smoother(raw / 0.34) : 1 - smoother((raw - 0.34) / 0.66);
    return { env, raw };
  };

  const ambientCalm = (now) => {
    const hold = Number(window.__ccCartAmbientHoldUntil) || 0;
    if (now < hold) return 0.3;
    return mix(0.3, 1, smoother((now - hold) / 1400));
  };

  /* ---------- The stone, drawn into any 2D context ----------
     One renderer for the cart icon and the add-to-cart flight, so the stone that
     flies is the stone in the bag. Coordinates are whatever space ctx is in: the
     icon passes its 100-unit bag space, the flight passes CSS pixels. `edge`
     scales the facet lines with the stone (1 = the icon's own weight). */
  const drawGlint = (ctx, x, y, size, alpha) => {
    if (alpha <= 0.01) return;
    ctx.save();
    ctx.translate(x, y);
    ctx.globalAlpha = alpha;
    const star = (len, width) => {
      ctx.beginPath();
      ctx.moveTo(0, -len);
      ctx.quadraticCurveTo(width, -width, len, 0);
      ctx.quadraticCurveTo(width, width, 0, len);
      ctx.quadraticCurveTo(-width, width, -len, 0);
      ctx.quadraticCurveTo(-width, -width, 0, -len);
      ctx.fill();
    };
    const core = ctx.createRadialGradient(0, 0, 0, 0, 0, size);
    core.addColorStop(0, 'rgba(255, 255, 255, 1)');
    core.addColorStop(0.3, 'rgba(255, 252, 240, 0.95)');
    core.addColorStop(1, 'rgba(226, 196, 140, 0)');
    ctx.fillStyle = core;
    star(size, size * 0.09);
    ctx.rotate(Math.PI / 4);
    star(size * 0.5, size * 0.08);
    ctx.restore();
  };

  const drawKohinoor = (ctx, { cx, cy, r, spin, tilt, sparkle = 0, exposure = 1, edge = 1 }) => {
    const cosS = Math.cos(spin);
    const sinS = Math.sin(spin);
    const cosT = Math.cos(tilt);
    const sinT = Math.sin(tilt);
    /* gem space -> view space: turn about the stone's own axis, then tip the
       table toward the viewer. Directions use the same rotation. */
    const toView = (p) => {
      const x = p[0] * cosS - p[2] * sinS;
      const z = p[0] * sinS + p[2] * cosS;
      return [x, p[1] * cosT + z * sinT, -p[1] * sinT + z * cosT];
    };
    const toGem = (d) => {
      const y = d[1] * cosT - d[2] * sinT;
      const z = d[1] * sinT + d[2] * cosT;
      return [d[0] * cosS + z * sinS, y, -d[0] * sinS + z * cosS];
    };
    const view = GEM.vertices.map(toView);
    const screen = view.map(([x, y]) => [cx + x * r, cy + y * r]);
    const eye = [0, 0, -1];

    const eyeGem = toGem(eye);

    /* Trace one wavelength from a point on a facet: refract in, then bounce
       (total internal reflection) until the ray can leave the stone. */
    const traceInside = (start, inside, ior) => {
      let p = start;
      let d = inside;
      for (let bounce = 0; bounce < 6; bounce += 1) {
        const hit = hitSurface(p, d);
        if (!hit) break;
        const out = refract(d, [-hit.n[0], -hit.n[1], -hit.n[2]], ior);
        if (out) return studio(toView(out));
        d = reflect(d, hit.n);
        p = hit.q;
      }
      return 0.05;
    };

    const shade = (face) => {
      const nView = toView(face.normal);
      const fresnel = F0 + (1 - F0) * Math.pow(1 - nView[2], 5);
      const mirror = studio(reflect(eye, nView));
      const rgb = [0, 0, 0];
      for (let c = 0; c < 3; c += 1) {
        const inside = refract(eyeGem, face.normal, 1 / IOR[c]);
        const light = inside ? traceInside(face.centre, inside, IOR[c]) : 0.05;
        const value = (fresnel * mirror + (1 - fresnel) * light) * exposure;
        rgb[c] = Math.pow(1 - Math.exp(-value * 2.2), 1.25);
      }
      /* Fire shows only where a facet is bright, and only as a tint. */
      const lum = (rgb[0] + rgb[1] + rgb[2]) / 3;
      const fire = 0.4 * smooth(0.66, 0.92, lum);
      return rgb.map((v) => mix(lum, v, fire));
    };

    ctx.save();
    ctx.lineJoin = 'round';
    for (const face of GEM.faces) {
      if (toView(face.normal)[2] <= 0.002) continue;

      const [red, green, blue] = shade(face);
      ctx.beginPath();
      face.v.forEach((i, k) => (k ? ctx.lineTo(screen[i][0], screen[i][1]) : ctx.moveTo(screen[i][0], screen[i][1])));
      ctx.closePath();
      ctx.fillStyle = `rgb(${mix(DARK[0], 255, red) | 0}, ${mix(DARK[1], 255, green) | 0}, ${mix(DARK[2], 255, blue) | 0})`;
      ctx.fill();
      /* Table pieces are seamed in their own colour. Other facet edges are cut
         in a fine line that always contrasts with its facet — graphite on the
         bright ones, light on the dark — so the kites stay legible on the
         pavilion as well as the crown. */
      const lit = (red + green + blue) / 3 > 0.55;
      ctx.strokeStyle = face.v.length === 3 && face.normal[1] < -0.99
        ? ctx.fillStyle
        : (lit ? 'rgba(52, 58, 72, 0.24)' : 'rgba(255, 255, 255, 0.18)');
      ctx.lineWidth = 0.3 * edge;
      ctx.stroke();
    }

    /* The girdle: the fine band where crown meets pavilion, drawn across the
       near half of the stone. It is the line that tells the eye "cut stone". */
    ctx.beginPath();
    for (let j = 0; j < GEM.girdle.length; j += 1) {
      const a = GEM.girdle[j];
      const b = GEM.girdle[(j + 1) % GEM.girdle.length];
      if (view[a][2] < -0.02 || view[b][2] < -0.02) continue;
      ctx.moveTo(screen[a][0], screen[a][1]);
      ctx.lineTo(screen[b][0], screen[b][1]);
    }
    ctx.strokeStyle = 'rgba(56, 62, 74, 0.38)';
    ctx.lineWidth = 0.42 * edge;
    ctx.stroke();

    const hull = convexHull(screen);
    ctx.beginPath();
    hull.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y)));
    ctx.closePath();
    ctx.strokeStyle = 'rgba(56, 62, 74, 0.42)';
    ctx.lineWidth = 0.55 * edge;
    ctx.stroke();
    ctx.restore();

    drawGlint(ctx, cx - r * 0.42, cy - r * 0.34, r * 0.5 * (0.75 + sparkle * 0.45), sparkle);
    return { cx, cy, r };
  };

  /* The stone's turn at any moment: the icon's slow revolution plus the half turn
     it makes on receive. Shared, so a flying stone is in step with the bag's. */
  const angleAt = (now) => {
    const receive = receiveEnvelope(now);
    return ((now - EPOCH) / REVOLUTION) * TAU + (receive.raw >= 0 ? smoother(receive.raw) * Math.PI : 0);
  };

  window.__fcKohinoor = Object.freeze({ draw: drawKohinoor, angleAt, stone: STONE });

  function initCartMotion(host) {
    if (!host || host.dataset.ccCartReady === 'true') return;
    const canvas = host.querySelector('.cc-cart-motion__canvas');
    if (!canvas) return;
    const ctx = canvas.getContext('2d', { alpha: true });
    if (!ctx) return;
    host.dataset.ccCartReady = 'true';

    let unit = 1;
    const resize = () => {
      const rect = host.getBoundingClientRect();
      const css = Math.max(24, Math.round(rect.width || 40));
      const dpr = Math.min(window.devicePixelRatio || 1, 3);
      const px = Math.round(css * dpr);
      if (canvas.width !== px) {
        canvas.width = px;
        canvas.height = px;
      }
      unit = px / 100;
    };
    resize();

    /* Gradients live in the 100-unit space and follow the canvas transform.
       Colours come from the shared gold palette in fifth-collective-cart-motion.css. */
    const palette = getComputedStyle(document.documentElement);
    const gold = (name, fallback) => palette.getPropertyValue(name).trim() || fallback;
    const GOLD = {
      shine: gold('--fc-gold-shine', '#fbefd0'),
      light: gold('--fc-gold-light', '#e7cc92'),
      mid: gold('--fc-gold', '#c79c5b'),
      dark: gold('--fc-gold-dark', '#a97e41'),
      edge: gold('--fc-gold-edge', '#d6b675'),
      shadowLight: gold('--fc-gold-shadow-light', '#9a7238'),
      shadow: gold('--fc-gold-shadow', '#7c5728'),
      shadowDark: gold('--fc-gold-shadow-dark', '#6a4920')
    };
    const metalShadow = ctx.createLinearGradient(0, 10, 0, 90);
    metalShadow.addColorStop(0, GOLD.shadowLight);
    metalShadow.addColorStop(0.5, GOLD.shadow);
    metalShadow.addColorStop(1, GOLD.shadowDark);
    const metalBody = ctx.createLinearGradient(10, 10, 90, 90);
    metalBody.addColorStop(0, GOLD.light);
    metalBody.addColorStop(0.35, GOLD.mid);
    metalBody.addColorStop(0.62, GOLD.dark);
    metalBody.addColorStop(1, GOLD.edge);

    let pressStartedAt = -Infinity;
    let running = !(reduceMotion && reduceMotion.matches);
    let raf = 0;
    let calmLevel = null;
    let lastFrameAt = 0;
    let resizeObserver = null;

    const strokeMetal = (trace, scale = 1) => {
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.beginPath();
      trace(ctx);
      ctx.strokeStyle = metalShadow;
      ctx.lineWidth = 3.6 * scale;
      ctx.stroke();
      ctx.strokeStyle = metalBody;
      ctx.lineWidth = 2.4 * scale;
      ctx.stroke();
      ctx.save();
      ctx.translate(-0.42, -0.55);
      ctx.globalAlpha = 0.75;
      ctx.strokeStyle = GOLD.shine;
      ctx.lineWidth = 0.8 * scale;
      ctx.beginPath();
      trace(ctx);
      ctx.stroke();
      ctx.restore();
    };

    const drawGrommets = () => {
      for (const [x, y] of GROMMETS) {
        ctx.beginPath();
        ctx.arc(x, y, 3.1, 0, TAU);
        ctx.fillStyle = metalBody;
        ctx.fill();
        ctx.lineWidth = 0.9;
        ctx.strokeStyle = GOLD.shadow;
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(x + 0.15, y + 0.2, 1.35, 0, TAU);
        ctx.fillStyle = 'rgba(48, 32, 12, 0.85)';
        ctx.fill();
      }
    };

    const drawSweep = (progress) => {
      if (progress <= 0 || progress >= 1) return;
      const p = mix(-0.2, 1.2, smoother(progress));
      const sweep = ctx.createLinearGradient(0, 0, 100, 100);
      const stop = (o, a) => sweep.addColorStop(clamp(o), `rgba(255, 249, 232, ${a})`);
      stop(p - 0.14, 0);
      stop(p - 0.04, 0.55);
      stop(p, 0.95);
      stop(p + 0.04, 0.55);
      stop(p + 0.14, 0);
      ctx.save();
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = sweep;
      ctx.lineWidth = 2;
      for (const trace of [traceBody, traceHandle, traceFlap]) {
        ctx.beginPath();
        trace(ctx);
        ctx.stroke();
      }
      ctx.restore();
    };

    const drawGlow = (cx, cy, radius, strength) => {
      if (strength <= 0.01) return;
      const glow = ctx.createRadialGradient(cx, cy, 0, cx, cy, radius);
      glow.addColorStop(0, `rgba(255, 240, 205, ${Math.min(0.85, 0.62 * strength)})`);
      glow.addColorStop(0.45, `rgba(232, 196, 128, ${Math.min(0.42, 0.28 * strength)})`);
      glow.addColorStop(1, 'rgba(232, 196, 128, 0)');
      ctx.fillStyle = glow;
      ctx.beginPath();
      ctx.arc(cx, cy, radius, 0, TAU);
      ctx.fill();
    };

    /* The stone is the shared drawKohinoor; here it sits in the bag's 100-unit space. */
    const drawDiamond = (spin, tilt, scale, sparkle, exposure) => {
      const r = STONE.r * scale;
      /* Swell about the stone's visual centre so it grows in place inside the V. */
      const cy = STONE.pivotY + (STONE.y - STONE.pivotY) * scale;
      return drawKohinoor(ctx, { cx: STONE.x, cy, r, spin, tilt, sparkle, exposure });
    };

    function frame(now, still = false) {
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.setTransform(unit, 0, 0, unit, 0, 0);

      const t = now - EPOCH;
      const receive = still ? { env: 0, raw: -1 } : receiveEnvelope(now);
      const pressAge = now - pressStartedAt;
      const press = still || pressAge < 0 || pressAge > PRESS_DURATION ? 0 : bell(pressAge / PRESS_DURATION);

      /* Ambient pulse: a slow swell that lingers at both ends. When an add-to-cart
         flight begins, the pulse eases down (never snaps) so the receive reads clearly. */
      const calmTarget = ambientCalm(now);
      if (calmLevel === null) calmLevel = calmTarget;
      else calmLevel += (calmTarget - calmLevel) * (1 - Math.exp(-Math.max(0, now - lastFrameAt) / 420));
      lastFrameAt = now;
      const calm = still ? 0 : calmLevel;
      const breath = still ? 0.5 : smoother(0.5 - 0.5 * Math.cos((t / PULSE) * TAU));
      const pulse = breath * calm;

      /* The still frame (reduced motion, and the static SVG twin traced from it)
         picks a turn where the stone is bright with crisp dark accents, at its
         resting size rather than the bottom of the pulse. */
      const angle = still ? 1.25 : angleAt(now);
      /* A pronounced swell: from 84% to 142%, the stone brightening as it grows. */
      const diamondScale = still ? 1 : 0.84 + pulse * 0.58 + receive.env * 0.3 - press * 0.06;
      const exposure = 1 + pulse * 0.4 + receive.env * 0.35;
      const bagScale = 1 + receive.env * 0.1 - press * 0.05 + pulse * 0.012;
      const sweepAge = t % SWEEP_EVERY;
      const sweep = still ? 0 : (sweepAge < SWEEP_LENGTH ? sweepAge / SWEEP_LENGTH : 0);
      /* A glint blooms at the height of each pulse, and once on receive. */
      const pulseGlint = smoother((pulse - 0.72) / 0.28);
      const receiveGlint = receive.raw >= 0 ? bell((receive.raw - 0.08) / 0.8) : 0;
      const sparkle = still ? 0 : Math.max(pulseGlint, receiveGlint);

      ctx.save();
      ctx.translate(50, 56);
      ctx.scale(bagScale, bagScale);
      ctx.translate(-50, -56);

      const tilt = STONE.tilt + pulse * STONE.nod + receive.env * STONE.nod * 0.6;

      drawGlow(STONE.x, STONE.pivotY, 22 * (0.8 + pulse * 0.75 + receive.env * 0.5), 0.25 + pulse * 0.95 + receive.env * 0.6);
      strokeMetal(traceHandle);
      strokeMetal(traceBody);
      drawGrommets();
      strokeMetal(traceFlap, 0.9);
      drawSweep(sweep);
      drawDiamond(angle, tilt, diamondScale, sparkle, exposure);

      ctx.restore();
      if (!host.classList.contains('is-ready')) host.classList.add('is-ready');
    }

    const tick = (now) => {
      raf = 0;
      /* Stop immediately once Shopify swaps this icon out of the document. */
      if (!host.isConnected) {
        if (resizeObserver) resizeObserver.disconnect();
        return;
      }
      if (!running) return;
      frame(now);
      raf = requestAnimationFrame(tick);
    };

    const ensureLoop = () => {
      if (!running || raf || !host.isConnected) return;
      raf = requestAnimationFrame(tick);
    };

    const triggerPress = () => {
      if (reduceMotion && reduceMotion.matches) return;
      pressStartedAt = performance.now();
    };

    const triggerExternalReceive = () => {
      if (reduceMotion && reduceMotion.matches) return;
      const startedAt = performance.now();
      window.__ccCartReceiveStartedAt = startedAt;
      window.__ccCartReceiveDuration = RECEIVE_DURATION;
      window.__ccCartAmbientHoldUntil = Math.max(
        Number(window.__ccCartAmbientHoldUntil) || 0,
        startedAt + RECEIVE_DURATION + 1600
      );
    };

    host.__ccCartMotion = { triggerPress, triggerExternalReceive };

    const cartLink = host.closest('#cart-icon-bubble');
    if (cartLink && !cartLink.dataset.ccCartPressBound) {
      cartLink.dataset.ccCartPressBound = 'true';
      cartLink.addEventListener('pointerdown', () => {
        const liveHost = cartLink.querySelector('[data-cc-cart-motion]');
        if (liveHost && liveHost.__ccCartMotion) liveHost.__ccCartMotion.triggerPress();
      }, { passive: true });
    }

    const applyMotionPreference = () => {
      running = !(reduceMotion && reduceMotion.matches);
      if (!running) {
        if (raf) cancelAnimationFrame(raf);
        raf = 0;
        frame(performance.now(), true);
      } else {
        ensureLoop();
      }
    };

    if (reduceMotion && typeof reduceMotion.addEventListener === 'function') {
      reduceMotion.addEventListener('change', applyMotionPreference);
    }

    if (typeof ResizeObserver === 'function') {
      resizeObserver = new ResizeObserver(() => {
        if (!host.isConnected) return resizeObserver.disconnect();
        resize();
        if (!running) frame(performance.now(), true);
      });
      resizeObserver.observe(host);
    }

    frame(performance.now(), !running);
    applyMotionPreference();
  }

  const refresh = () => document.querySelectorAll('[data-cc-cart-motion]').forEach((host) => initCartMotion(host));

  let refreshQueued = false;
  const queueRefresh = () => {
    if (refreshQueued) return;
    refreshQueued = true;
    requestAnimationFrame(() => {
      refreshQueued = false;
      refresh();
    });
  };

  refresh();

  new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      const target = mutation.target instanceof Element ? mutation.target : mutation.target.parentElement;
      if (target && target.closest && target.closest('#cart-icon-bubble')) return queueRefresh();
      for (const node of mutation.addedNodes) {
        if (
          node.nodeType === 1 &&
          (node.id === 'cart-icon-bubble' ||
            (node.matches && node.matches('[data-cc-cart-motion]')) ||
            (node.querySelector && node.querySelector('[data-cc-cart-motion]')))
        ) {
          return queueRefresh();
        }
      }
    }
  }).observe(document.documentElement, { childList: true, subtree: true });

  document.addEventListener('shopify:section:load', queueRefresh);
})();
